import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    Checkbox,
    CheckboxGroup,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Input,
    NumberInput,
    NumberInputField,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    Text,
    useColorModeValue,
    useToast,
    VStack,
} from "@chakra-ui/react";
import FormModal from "../../../components/FormModal";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 } from 'uuid';
import FormField from "../../../components/fields/FormField";
import { translate, TranslationKeys } from "../../../i18n";
import { Currency } from "../../../models/court-pricing/dto/court-pricing-rule.dto";
import {
    BatchCreateCourtBlockadeSkipReason,
    BatchCreateCourtBlockadesDto,
    BatchCreateCourtBlockadesResultDto,
} from "../../../models/court/dto/batch-create-court-blockades.dto";
import { CourtAvailabilityDto } from "../../../models/court/dto/court-availability.dto";
import { CourtDto, SportType } from "../../../models/court/dto/court.dto";
import { CourtTemporaryBlockadeDto, CourtTemporaryBlockadeReason } from "../../../models/court/dto/court-temporary-blockade.dto";
import { CreateCourtBlockadeDto } from "../../../models/court/dto/create-court-blockade.dto";
import CourtAvailabilityTypePicker from "../../court-management/components/CourtAvailabilityTypePicker";
import SportPicker from "../../court-management/components/SportPicker";
import { useAddAvailabilities } from "../../court-management/hooks/useAddAvailabilities";
import { getDefaultCurrency } from "../../court-pricing-management/components/CurrencyPicker";
import DurationInput from "../../court-pricing-management/components/DurationInput";
import PriceInput from "../../court-pricing-management/components/PriceInput";
import CreateEventForm from "../../event-management/components/CreateEventForm";
import { MAX_BOOKING_CAPACITY, MIN_BOOKING_CAPACITY, WILDCARD_CAPACITY, capacityFieldValue } from "../../../utils/sport-capacity";
import {
    availabilitySubmitBlockerToast,
    findAvailabilitySubmitBlocker,
} from "../../court-management/utils/availability-submit-blocker";
import { parsePrice } from "../../../utils/parse-price";
import { useBatchCreateCourtBlockades } from '../hooks/useBatchCreateCourtBlockades';
import { useCreateCourtBlockade } from '../hooks/useCreateCourtBlockade';
import { typography } from 'theme/tokens';
import BlockadeReasonPicker, { courtBlockadeReasonEnumToString } from "./BlockadeReasonPicker";

const CONFIRMATION_THRESHOLD = 20;
const MAX_REPEAT_WEEKS = 25;

const SKIP_REASON_KEY: Record<BatchCreateCourtBlockadeSkipReason, TranslationKeys> = {
    OVERLAP_WITH_BOOKING: 'courts.blockade.batch.skip_reason.OVERLAP_WITH_BOOKING',
    OVERLAP_WITH_GAME: 'courts.blockade.batch.skip_reason.OVERLAP_WITH_GAME',
    OVERLAP_WITH_BLOCKADE: 'courts.blockade.batch.skip_reason.OVERLAP_WITH_BLOCKADE',
    OVERLAP_WITH_PENDING_HOLD: 'courts.blockade.batch.skip_reason.OVERLAP_WITH_PENDING_HOLD',
    COURT_NOT_FOUND: 'courts.blockade.batch.skip_reason.COURT_NOT_FOUND',
};

type PricingConfiguration = {
    id: string;
    sport: SportType;
    slotDurationInMs: number;
    price: number;
    currency: Currency;
    capacity?: number;
};


type Props = {
    isModalVisible: boolean;
    setIsModalVisible: (newValue: boolean) => void;
    onBlockadeCreated: (newBlockade: CourtTemporaryBlockadeDto) => void;
    onCourtAvailabilitiesAdded: (items: CourtAvailabilityDto[]) => void;
    courtId: number;
    selectedTimeslot?: SelectedTimeSlotForBlockadeProps;
    /**
     * All courts available at the current location. Required to enable the
     * multi-court batch sub-mode; when absent, batch mode operates on just the
     * anchor court (date extension only).
     */
    courts?: CourtDto[];
    /** Called after an event is created from the calendar slot (so the calendar can refetch). */
    onEventCreated?: () => void;
    /**
     * Called when the operator picks the "Book for a player" radio. The
     * parent is expected to close this modal and open
     * CreateAdminBookingModal pre-filled with the dragged timeslot. When
     * omitted, the admin-booking radio is hidden (e.g., for tenants that
     * don't have a current org context loaded yet).
     */
    onCreateAdminBooking?: (timeslot: SelectedTimeSlotForBlockadeProps) => void;
    /**
     * Whether the signed-in user may perform org-management actions
     * (`useIsOrgManager`). Only the blockade mode is staff-level on the API
     * (`hasOrganizationStaffAccess` — admin or coach); availability, event and
     * admin-booking creation are `hasOrganizationAdminAccess`. When false the
     * mode chooser is hidden and the modal is a plain "block this court" form,
     * so a coach never sees a radio whose submit 403s. Defaults to true so the
     * existing admin call sites are unchanged.
     */
    isOrgManager?: boolean;
};

export type SelectedTimeSlotForBlockadeProps = { startTime: Date; endTime: Date; courtId: number };

function isoDateOnly(d: Date): string {
    // Anchor date as seen in the user's local time zone (matches what they clicked).
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
}

function computeRepeatWeeks(anchorDate: string, repeatUntil: string): number {
    if (!anchorDate || !repeatUntil) return 0;
    const anchor = new Date(anchorDate + 'T00:00:00Z');
    const until = new Date(repeatUntil + 'T00:00:00Z');
    if (isNaN(anchor.getTime()) || isNaN(until.getTime()) || until < anchor) return 0;
    const diffDays = Math.floor(
        (until.getTime() - anchor.getTime()) / (24 * 60 * 60 * 1000),
    );
    return Math.floor(diffDays / 7);
}

function dedupeExpandedDates(baseDates: string[], repeatWeeks: number): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const d of baseDates) {
        for (let w = 0; w <= repeatWeeks; w++) {
            const base = new Date(d + 'T00:00:00Z');
            base.setUTCDate(base.getUTCDate() + w * 7);
            const iso = base.toISOString().slice(0, 10);
            if (!seen.has(iso)) {
                seen.add(iso);
                out.push(iso);
            }
        }
    }
    return out;
}

function getBrowserTimezone(): string {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

export default function CreateBlockadeOrCourtAvailabilityModal({
    courtId,
    courts,
    selectedTimeslot,
    isModalVisible,
    onCourtAvailabilitiesAdded,
    onBlockadeCreated,
    setIsModalVisible,
    onEventCreated,
    onCreateAdminBooking,
    isOrgManager = true,
}: Props) {
    const toast = useToast();
    const labelColor = useColorModeValue('secondaryGray.900', 'white');
    const batchPreviewBg = useColorModeValue('blue.50', 'blue.900');

    // Single-create mutation.
    const { mutateAsync: createBlockade, isLoading: isCreatingBlockade } = useCreateCourtBlockade((newBlockade) => {
        setIsModalVisible(false);
        onBlockadeCreated(newBlockade);
    });

    // Batch-create mutation. On success we do NOT close the modal — we swap the
    // form for the 3-state result card.
    const [batchResult, setBatchResult] = useState<BatchCreateCourtBlockadesResultDto | null>(null);
    const { mutateAsync: batchCreate, isLoading: isBatchCreating } = useBatchCreateCourtBlockades(
        (data) => setBatchResult(data),
    );

    const [creationMode, setCreationMode] = useState<
        'blockade' | 'court-availability' | 'event' | 'admin-booking'
    >('blockade');

    // Reset to the default mode every time the modal opens. Without this,
    // an operator who picked "Réserver pour un joueur" (which sets
    // creationMode='admin-booking', closes the modal, and hands off to
    // CreateAdminBookingModal) would on the NEXT drag see the chooser
    // open with creationMode still 'admin-booking' — the body wouldn't
    // render any of the blockade/availability/event forms, and the title
    // would fall through to "Créer un évenement" via the catch-all.
    useEffect(() => {
        if (isModalVisible) {
            setCreationMode('blockade');
        }
    }, [isModalVisible]);
    const [isGameAvailable, setIsGameAvailable] = useState(true);
    const [isCoachingAvailable, setIsCoachingAvailable] = useState(false);
    const [isEventFormOpen, setIsEventFormOpen] = useState(false);

    const { isLoading: isAddingAvailabilities, mutateAsync: addAvailabilities } = useAddAvailabilities((data) => {
        setIsModalVisible(false);
        onCourtAvailabilitiesAdded(data);
    }, (error) => {
        // The API names the offending row ("Invalid availability pricing
        // capacity \"30\"…"); this used to print a two-word "Erreur" over it,
        // leaving the operator to guess which of their configurations was
        // refused. Same treatment as the sibling batch save.
        toast({
            title: translate('error'),
            description: error instanceof Error ? error.message : undefined,
            status: 'error',
            duration: 8000,
            isClosable: true,
            position: 'top-right',
        });
    });

    const [createBlockadeDto, setCreateBlockadeDto] = useState<CreateCourtBlockadeDto>();
    const [configurations, setConfigurations] = useState<PricingConfiguration[]>([{
        id: v4(),
        sport: SportType.PADEL,
        slotDurationInMs: 60 * 60 * 1000,
        price: 0,
        currency: getDefaultCurrency(),
        capacity: undefined
    }]);

    // Batch sub-mode state (only relevant when creationMode === 'blockade').
    const [isBatchMode, setIsBatchMode] = useState(false);
    const [selectedCourtIds, setSelectedCourtIds] = useState<number[]>([]);
    const [repeatUntil, setRepeatUntil] = useState('');
    const [courtFilter, setCourtFilter] = useState('');
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!selectedTimeslot) return;
        setCreateBlockadeDto({
            courtId: selectedTimeslot.courtId,
            startTime: selectedTimeslot.startTime.toISOString(),
            endTime: selectedTimeslot.endTime.toISOString(),
            description: '',
            reason: CourtTemporaryBlockadeReason.OTHER
        });
        // Prime batch-mode defaults from the anchor slot.
        setSelectedCourtIds([selectedTimeslot.courtId]);
        setRepeatUntil('');
        setIsBatchMode(false);
        setBatchResult(null);
    }, [selectedTimeslot]);

    const handleInputChange = useCallback((key: keyof typeof createBlockadeDto, value: any) => {
        setCreateBlockadeDto(prev => {
            return { ...prev, [key]: value };
        });
    }, [setCreateBlockadeDto]);

    /**
     * Save the availability the operator drew on the calendar — after the same
     * two guards every other pricing-row writer runs.
     *
     * This modal was the ONE writer with no guard at all: an empty price field
     * (`parsePrice('')` → `NaN`) or two rows for the same sport + duration +
     * capacity went straight to the API, which rejects the whole batch with a
     * 400. The operator saw a toast carrying the server's sentence and had to
     * work out which of their rows it meant. Now the offending capacity is
     * named before anything is sent, and nothing is sent.
     *
     * One group: this modal creates exactly ONE availability block, so a
     * repeated triple inside it is always ambiguous (two blocks may legitimately
     * repeat one — see `findAvailabilityCapacityIssues`).
     */
    const submitAvailability = useCallback(() => {
        if (!selectedTimeslot) return;

        const blocker = findAvailabilitySubmitBlocker(
            [configurations],
            configurations.map(c => c.price),
        );
        if (blocker) {
            const { title, durationMs } = availabilitySubmitBlockerToast(blocker);
            toast({
                title,
                status: 'error',
                duration: durationMs,
                isClosable: true,
                position: 'top-right',
            });
            return;
        }

        addAvailabilities({
            courtId, availabilities: [{
                courtId,
                startTime: selectedTimeslot.startTime.toISOString(),
                endTime: selectedTimeslot.endTime.toISOString(),
                isGameAvailable,
                isCoachingAvailable,
                id: v4(),
                dayOfWeek: selectedTimeslot.startTime.getDay(),
                pricings: configurations
            }]
        });
    }, [
        addAvailabilities,
        configurations,
        courtId,
        isCoachingAvailable,
        isGameAvailable,
        selectedTimeslot,
        toast,
    ]);

    const availableCourts = useMemo(() => courts ?? [], [courts]);
    const filteredCourts = useMemo(() => {
        const q = courtFilter.trim().toLowerCase();
        if (!q) return availableCourts;
        return availableCourts.filter((c) => c.name.toLowerCase().includes(q));
    }, [availableCourts, courtFilter]);
    const allFilteredSelected =
        filteredCourts.length > 0 &&
        filteredCourts.every((c) => selectedCourtIds.includes(c.id));

    const anchorDate = selectedTimeslot ? isoDateOnly(selectedTimeslot.startTime) : '';
    const baseDates = useMemo(
        () => (anchorDate ? [anchorDate] : []),
        [anchorDate],
    );
    const repeatWeeksCount = useMemo(
        () => computeRepeatWeeks(anchorDate, repeatUntil),
        [anchorDate, repeatUntil],
    );
    const exceedsMaxRepeat = repeatWeeksCount > MAX_REPEAT_WEEKS;
    const expandedDates = useMemo(
        () => dedupeExpandedDates(baseDates, exceedsMaxRepeat ? MAX_REPEAT_WEEKS : repeatWeeksCount),
        [baseDates, repeatWeeksCount, exceedsMaxRepeat],
    );
    const totalRequested = selectedCourtIds.length * expandedDates.length;

    const canBatchSubmit =
        isBatchMode &&
        selectedCourtIds.length > 0 &&
        expandedDates.length > 0 &&
        !exceedsMaxRepeat &&
        !isBatchCreating &&
        !batchResult;

    // ---- Batch submission helpers ----

    const buildBatchDto = (): BatchCreateCourtBlockadesDto | null => {
        if (!selectedTimeslot || !createBlockadeDto) return null;
        const start = selectedTimeslot.startTime;
        const end = selectedTimeslot.endTime;
        return {
            courtIds: selectedCourtIds,
            dates: baseDates,
            repeatWeekly: repeatWeeksCount > 0 ? { count: repeatWeeksCount } : undefined,
            timeOfDay: {
                startHour: start.getHours(),
                startMinute: start.getMinutes(),
                endHour: end.getHours(),
                endMinute: end.getMinutes(),
            },
            timezone: getBrowserTimezone(),
            reason: createBlockadeDto.reason,
            description: createBlockadeDto.description,
        };
    };

    const doBatchSubmit = async () => {
        const dto = buildBatchDto();
        if (!dto) return;
        await batchCreate(dto);
    };

    const onBatchSubmitClick = () => {
        if (totalRequested > CONFIRMATION_THRESHOLD) {
            setIsConfirmOpen(true);
            return;
        }
        void doBatchSubmit();
    };

    const closeAndReset = () => {
        setBatchResult(null);
        setIsModalVisible(false);
    };

    if (!createBlockadeDto) return null;

    // Helper function to convert Date object to datetime-local string
    const toLocalDateTimeStringFromDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // ---- Batch result takeover ----

    if (batchResult) {
        const variant: 'green' | 'amber' | 'red' =
            batchResult.skipped.length === 0
                ? 'green'
                : batchResult.created.length === 0
                ? 'red'
                : 'amber';
        const bannerBg =
            variant === 'green' ? 'green.50' : variant === 'amber' ? 'orange.50' : 'red.50';
        const bannerBorder =
            variant === 'green' ? 'green.400' : variant === 'amber' ? 'orange.400' : 'red.400';
        const heading =
            variant === 'green'
                ? translate('courts.blockade.batch.result_all_created')
                : variant === 'amber'
                ? translate('courts.blockade.batch.result_some_skipped')
                : translate('courts.blockade.batch.result_nothing_created');

        return (
            <FormModal
                isOpen={isModalVisible}
                onClose={closeAndReset}
                title={translate('courts.blockade.batch.result_title')}
                size="3xl"
                submitLabel={translate('courts.blockade.batch.result_done')}
                isSubmitting={false}
                onSubmit={closeAndReset}
            >
                <Box p={4} borderRadius="md" borderWidth={1} borderColor={bannerBorder} bg={bannerBg} mb={4}>
                    <Text fontWeight="bold" mb={2}>{heading}</Text>
                    <Text>
                        {translate('courts.blockade.batch.result_summary', {
                            created: batchResult.created.length,
                            total: batchResult.totalRequested,
                            skipped: batchResult.skipped.length,
                        })}
                    </Text>
                </Box>
                {batchResult.skipped.length > 0 && (
                    <Box>
                        <Text fontWeight="semibold" mb={2}>{translate('courts.blockade.batch.skipped_pairs_heading')}</Text>
                        <VStack align="stretch" spacing={1} maxH="280px" overflowY="auto">
                            {batchResult.skipped.map((s, i) => {
                                const court = availableCourts.find((c) => c.id === s.courtId);
                                const courtLabel = court?.name ?? translate('courts.blockade.batch.court_fallback_name', { id: s.courtId });
                                return (
                                    <Flex key={i} justify="space-between" fontSize="sm" px={2} py={1} borderBottomWidth={1}>
                                        <Text>{courtLabel + ' \u2014 ' + s.date}</Text>
                                        <Text color="gray.600">{translate(SKIP_REASON_KEY[s.reason])}</Text>
                                    </Flex>
                                );
                            })}
                        </VStack>
                    </Box>
                )}
            </FormModal>
        );
    }

    return (
        <>
            <FormModal
                isOpen={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                title={
                    creationMode === 'blockade'
                        ? translate('calendar.create_blockade_or_court_availability_modal.blockade_mode_title')
                        : creationMode === 'court-availability'
                        ? translate('calendar.create_blockade_or_court_availability_modal.add_availability_mode_title')
                        : creationMode === 'event'
                        ? translate('event.add_new')
                        : ''
                }
                size="3xl"
                submitLabel={translate('submit')}
                isSubmitting={isCreatingBlockade || isAddingAvailabilities || isBatchCreating}
                isSubmitDisabled={creationMode === 'blockade' && isBatchMode && !canBatchSubmit}
                onSubmit={() => {
                    if (creationMode === 'blockade') {
                        if (isBatchMode) {
                            onBatchSubmitClick();
                        } else {
                            createBlockade(createBlockadeDto);
                        }
                    } else {
                        submitAvailability();
                    }
                }}
            >
                {/* A coach only ever gets the blockade form: the other three
                    modes are admin-only on the API, and `creationMode` is
                    reset to 'blockade' on every open, so hiding the chooser
                    is enough — no other mode is reachable without it. */}
                {isOrgManager && (
                <FormControl>
                    <RadioGroup
                        onChange={(value) => {
                            const newMode = value as
                                | 'blockade'
                                | 'court-availability'
                                | 'event'
                                | 'admin-booking';
                            setCreationMode(newMode);
                            if (newMode === 'event') {
                                setIsEventFormOpen(true);
                                setIsModalVisible(false);
                            } else if (newMode === 'admin-booking') {
                                // Hand off to the parent — it owns
                                // CreateAdminBookingModal and the prefill
                                // state. Mirror the 'event' branch: close
                                // ourselves so the operator doesn't see two
                                // overlapping modals.
                                if (selectedTimeslot && onCreateAdminBooking) {
                                    onCreateAdminBooking(selectedTimeslot);
                                }
                                setIsModalVisible(false);
                            }
                        }}
                        value={creationMode}>
                        <Stack direction="row">
                            <Radio value="blockade">{translate('calendar.create_blockade_or_court_availability_modal.blockade')}</Radio>
                            <Radio value="court-availability">{translate('calendar.create_blockade_or_court_availability_modal.add_availability')}</Radio>
                            <Radio value="event">{translate('event.add_new')}</Radio>
                            {onCreateAdminBooking && (
                                <Radio value="admin-booking">
                                    {translate('calendar.create_blockade_or_court_availability_modal.admin_booking')}
                                </Radio>
                            )}
                        </Stack>
                    </RadioGroup>
                </FormControl>
                )}
                {
                    creationMode === 'court-availability' &&
                    <>
                        <CourtAvailabilityTypePicker
                            isGameAvailable={isGameAvailable}
                            setIsGameAvailable={setIsGameAvailable}
                            isCoachingAvailable={isCoachingAvailable}
                            setIsCoachingAvailable={setIsCoachingAvailable}
                        />
                        <VStack spacing={4} align="stretch" mt={4}>
                            {configurations.map((config, configIndex) => (
                                <Box key={config.id}>
                                    <Flex gap={4} align="flex-start">
                                        <FormControl>
                                            <SportPicker
                                                sport={config.sport}
                                                setSport={(newSport) => {
                                                    setConfigurations(prev => prev.map((c, i) =>
                                                        i === configIndex ? { ...c, sport: newSport[0] } : c
                                                    ));
                                                }}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <DurationInput
                                                durationInMs={config.slotDurationInMs}
                                                setDuration={newDuration => {
                                                    setConfigurations(prev => prev.map((c, i) =>
                                                        i === configIndex ? { ...c, slotDurationInMs: newDuration } : c
                                                    ));
                                                }}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <PriceInput
                                                name="court_pricing"
                                                value={config.price}
                                                onChange={(event) => {
                                                    setConfigurations(prev => prev.map((c, i) =>
                                                        i === configIndex ? { ...c, price: parsePrice(event.target.value) } : c
                                                    ));
                                                }}
                                                currency={config.currency}
                                                setCurrency={(newCurrency) => {
                                                    setConfigurations(prev => prev.map((c, i) =>
                                                        i === configIndex ? { ...c, currency: newCurrency } : c
                                                    ));
                                                }}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel {...typography.formLabel}>{translate('game_capacity_options.capacity')}</FormLabel>
                                            <NumberInput
                                                value={capacityFieldValue(config.capacity)}
                                                onChange={(valueString, valueAsNumber) => {
                                                    setConfigurations(prev => prev.map((c, i) =>
                                                        i === configIndex ? {
                                                            ...c,
                                                            capacity: valueString === '' ? undefined : valueAsNumber
                                                        } : c
                                                    ));
                                                }}
                                                min={MIN_BOOKING_CAPACITY}
                                                // Was a hardcoded 50, while the
                                                // API rejects anything above 20
                                                // on every write path — Chakra
                                                // clamps on blur, so 21–50 was
                                                // submittable and 400'd.
                                                max={MAX_BOOKING_CAPACITY}
                                                size="sm"
                                            >
                                                <NumberInputField placeholder={translate('courts.all_capacities')} />
                                            </NumberInput>
                                        </FormControl>
                                        <Button
                                            colorScheme="red"
                                            size="sm"
                                            onClick={() => {
                                                setConfigurations(prev => prev.filter((_, i) => i !== configIndex));
                                            }}
                                            isDisabled={configurations.length <= 1}
                                        >
                                            -
                                        </Button>
                                    </Flex>
                                    {configIndex < configurations.length - 1 && <Divider my={2} />}
                                </Box>
                            ))}
                            <Box>
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        setConfigurations(prev => [...prev, {
                                            id: v4(),
                                            sport: SportType.PADEL,
                                            slotDurationInMs: 60 * 60 * 1000,
                                            price: 0,
                                            currency: getDefaultCurrency(),
                                            // Same wildcard as the first row,
                                            // written the same way. This seeded
                                            // the `-1` sentinel while the first
                                            // row seeded `undefined` — two
                                            // spellings of "prices every
                                            // capacity" inside one form, and
                                            // the one the operator could see
                                            // was the one that rendered as a
                                            // capacity of minus one.
                                            capacity: undefined
                                        }]);
                                    }}
                                >
                                    {translate('add')}
                                </Button>
                            </Box>
                        </VStack>
                    </>
                }
                <FormControl>
                    {creationMode === 'blockade' && (
                        <>
                            {/* Single / Batch toggle */}
                            <Flex align="center" justify="space-between" mt={2} mb={3} py={2} borderTopWidth={1} borderBottomWidth={1}>
                                <Box>
                                    <Text fontWeight="semibold">{translate('courts.blockade.batch.toggle_title')}</Text>
                                    <Text fontSize="sm" color="gray.600">
                                        {translate('courts.blockade.batch.toggle_subtitle')}
                                    </Text>
                                </Box>
                                <Switch
                                    isChecked={isBatchMode}
                                    onChange={(e) => setIsBatchMode(e.target.checked)}
                                />
                            </Flex>

                            {isBatchMode && (
                                <Box
                                    mb={3}
                                    py={2}
                                    px={3}
                                    borderRadius="md"
                                    bg={batchPreviewBg}
                                >
                                    <Text fontWeight="bold" aria-live="polite">
                                        {translate('courts.blockade.batch.preview_count', {
                                            total: totalRequested,
                                            courts: selectedCourtIds.length,
                                            dates: expandedDates.length,
                                        })}
                                    </Text>
                                    <Text fontSize="sm" color="gray.700">
                                        {translate('courts.blockade.batch.time_window', {
                                            start: format(selectedTimeslot!.startTime, 'HH:mm', { locale: fr }),
                                            end: format(selectedTimeslot!.endTime, 'HH:mm', { locale: fr }),
                                        })}
                                    </Text>
                                </Box>
                            )}

                            <FormField
                                label={translate('courts.blockade.comment')}
                                value={createBlockadeDto.description}
                                onChange={(newValue: string) => handleInputChange('description', newValue)}
                            />

                            {!isBatchMode && (
                                <>
                                    <FormField
                                        label={translate('event.start_date')}
                                        value={format(new Date(createBlockadeDto.startTime), "dd MMMM yyyy HH:mm", { locale: fr })}
                                        isDisabled
                                        onChange={() => {}}
                                    />
                                    <FormField
                                        label={translate('event.end_date')}
                                        value={format(new Date(createBlockadeDto.endTime), "dd MMMM yyyy HH:mm", { locale: fr })}
                                        isDisabled
                                        onChange={() => {}}
                                    />
                                </>
                            )}

                            {isBatchMode && (
                                <Stack spacing={3} mb={3}>
                                    {availableCourts.length > 0 && (
                                        <FormControl>
                                            <FormLabel {...typography.formLabel} color={labelColor}>{translate('courts.blockade.batch.courts_label')}</FormLabel>
                                            <Input
                                                placeholder={translate('courts.blockade.batch.filter_placeholder')}
                                                value={courtFilter}
                                                onChange={(e) => setCourtFilter(e.target.value)}
                                                mb={2}
                                                size="sm"
                                            />
                                            <Box borderWidth={1} borderRadius="md" maxH="180px" overflowY="auto" p={2}>
                                                <Checkbox
                                                    isChecked={allFilteredSelected}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            const ids = new Set(selectedCourtIds);
                                                            filteredCourts.forEach((c) => ids.add(c.id));
                                                            setSelectedCourtIds(Array.from(ids));
                                                        } else {
                                                            const drop = new Set(filteredCourts.map((c) => c.id));
                                                            setSelectedCourtIds(selectedCourtIds.filter((id) => !drop.has(id)));
                                                        }
                                                    }}
                                                    mb={1}
                                                >
                                                    <Text fontWeight="semibold">{translate('courts.blockade.batch.select_all')}</Text>
                                                </Checkbox>
                                                <Divider mb={2} />
                                                <CheckboxGroup
                                                    value={selectedCourtIds.map(String)}
                                                    onChange={(values) =>
                                                        setSelectedCourtIds((values as string[]).map((v) => Number(v)))
                                                    }
                                                >
                                                    <Stack>
                                                        {filteredCourts.map((c) => (
                                                            <Checkbox key={c.id} value={String(c.id)}>{c.name}</Checkbox>
                                                        ))}
                                                    </Stack>
                                                </CheckboxGroup>
                                            </Box>
                                        </FormControl>
                                    )}

                                    <Box>
                                        <Text fontSize="sm" color="gray.600">
                                            {translate('courts.blockade.batch.starting_label')}
                                        </Text>
                                        <Text fontWeight="medium">
                                            {format(selectedTimeslot!.startTime, 'EEEE dd MMMM yyyy', { locale: fr })}
                                        </Text>
                                    </Box>

                                    <FormControl>
                                        <FormLabel {...typography.formLabel} color={labelColor}>
                                            {translate('courts.blockade.batch.repeat_until_label')}
                                        </FormLabel>
                                        <Input
                                            type="date"
                                            value={repeatUntil}
                                            min={anchorDate}
                                            onChange={(e) => setRepeatUntil(e.target.value)}
                                        />
                                        {exceedsMaxRepeat && (
                                            <Text fontSize="sm" color="red.500" mt={1}>
                                                {translate('courts.blockade.batch.exceeds_max_weeks')}
                                            </Text>
                                        )}
                                    </FormControl>
                                </Stack>
                            )}

                            <FormControl>
                                <FormLabel {...typography.formLabel} color={labelColor} mb="6px">
                                    {translate('calendar.create_blockade_or_court_availability_modal.blockade')}
                                </FormLabel>
                                <BlockadeReasonPicker
                                    selectedReason={createBlockadeDto.reason}
                                    setSelectedReason={(newValue) => handleInputChange('reason', newValue)}
                                />
                            </FormControl>
                        </>
                    )}
                </FormControl>
            </FormModal>

            <AlertDialog
                isOpen={isConfirmOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => setIsConfirmOpen(false)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">{translate('courts.blockade.batch.confirm_title')}</AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('courts.blockade.batch.confirm_body', {
                                total: totalRequested,
                                courts: selectedCourtIds.length,
                                dates: expandedDates.length,
                                reason: courtBlockadeReasonEnumToString(createBlockadeDto.reason),
                            })}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setIsConfirmOpen(false)}>{translate('courts.blockade.batch.cancel')}</Button>
                            <Button
                                colorScheme="blue"
                                onClick={() => {
                                    setIsConfirmOpen(false);
                                    void doBatchSubmit();
                                }}
                                ml={3}
                            >
                                {translate('courts.blockade.batch.confirm')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {selectedTimeslot && (
                <CreateEventForm
                    isOpen={isEventFormOpen}
                    onClose={() => {
                        setIsEventFormOpen(false);
                        setCreationMode('blockade');
                    }}
                    initialStartTime={toLocalDateTimeStringFromDate(selectedTimeslot.startTime)}
                    initialEndTime={toLocalDateTimeStringFromDate(selectedTimeslot.endTime)}
                    initialCourtId={courtId}
                    onEventCreated={onEventCreated}
                />
            )}
        </>
    );
}
